import React, { FC, useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import PageContainer from "../../../components/pageContainer";
import { FilterListOutlined, MoreVert, Add } from "@mui/icons-material";
import { DataGrid, GridColDef, GridRenderCellParams, GridPaginationModel } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { jaJP } from "@mui/x-data-grid/locales";
import { formatDate } from "../../../utils/datetimeUtils";
import { PATHS } from "../../../routes/paths";
import {
  listManufacturersAdminManufacturersGet,
  createManufacturerAdminManufacturersPost,
  deleteManufacturerAdminManufacturersManufacturerIdDelete
} from "../../../client/services.gen";
import { ManufacturerResponse } from "../../../client/types.gen";

interface ActionMenuProps {
  params: GridRenderCellParams<ManufacturerResponse>;
  onDelete: (id: number) => void;
}

const ActionMenu: React.FC<ActionMenuProps> = ({ params, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const navigate = useNavigate();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    navigate(
      `${PATHS.MANUFACTURER_DETAIL.replaceAll(':id', String(params.row.id))}`
    );
    handleClose();
  };

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
    handleClose();
  };

  const handleDeleteConfirm = () => {
    onDelete(params.row.id);
    setOpenDeleteDialog(false);
  };

  return (
    <>
      <IconButton
        aria-label="more"
        aria-controls={`menu-${params.id}`}
        aria-haspopup="true"
        onClick={handleClick}
      >
        <MoreVert />
      </IconButton>
      <Menu
        id={`menu-${params.id}`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <MenuItem onClick={handleEdit}>
          詳細
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          削除
        </MenuItem>
      </Menu>

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>削除確認</DialogTitle>
        <DialogContent>
          <Typography>
            「{params.row.name}」を削除してもよろしいですか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>キャンセル</Button>
          <Button onClick={handleDeleteConfirm} color="error">
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const ManufacturerList: FC = () => {
  const navigate = useNavigate();
  const [openRegisterDialog, setOpenRegisterDialog] = useState(false);
  const [manufacturers, setManufacturers] = useState<ManufacturerResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [newManufacturerName, setNewManufacturerName] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  // 製造メーカー一覧を取得
  const fetchManufacturers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listManufacturersAdminManufacturersGet({
        search: searchText || undefined
      });
      setManufacturers(response);
    } catch (err) {
      setError('製造メーカーの取得に失敗しました');
      console.error('Failed to fetch manufacturers:', err);
    } finally {
      setLoading(false);
    }
  };

  // 製造メーカーを削除
  const handleDelete = async (id: number) => {
    try {
      await deleteManufacturerAdminManufacturersManufacturerIdDelete({
        manufacturerId: id
      });
      await fetchManufacturers();
    } catch (err) {
      setError('製造メーカーの削除に失敗しました');
      console.error('Failed to delete manufacturer:', err);
    }
  };

  // 製造メーカーを作成
  const handleCreate = async () => {
    if (!newManufacturerName.trim()) return;

    setCreateLoading(true);
    try {
      await createManufacturerAdminManufacturersPost({
        requestBody: {
          name: newManufacturerName
        }
      });
      setNewManufacturerName("");
      setOpenRegisterDialog(false);
      await fetchManufacturers();
    } catch (err) {
      setError('製造メーカーの作成に失敗しました');
      console.error('Failed to create manufacturer:', err);
    } finally {
      setCreateLoading(false);
    }
  };

  useEffect(() => {
    fetchManufacturers();
  }, [searchText]);

  const listColumns: GridColDef<ManufacturerResponse>[] = [
    {
      field: "name",
      headerName: "メーカー名",
      minWidth: 200,
      flex: 1,
      sortable: false,
    },
    {
      field: "updated_at",
      headerName: "更新日",
      minWidth: 150,
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <>{formatDate(params.row.updated_at)}</>
      ),
    },
    {
      field: "created_at",
      headerName: "作成日",
      minWidth: 150,
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <>{formatDate(params.row.created_at)}</>
      ),
    },
    {
      field: "actions",
      headerName: "",
      renderCell: (params) => (
        <ActionMenu params={params} onDelete={handleDelete} />
      ),
      width: 60,
      sortable: false,
    },
  ];

  return (
    <PageContainer>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5" component="div">
          メーカー管理
        </Typography>
        <Box display="flex" gap={1}>
          <TextField
            size="small"
            placeholder="メーカー名で検索..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: <FilterListOutlined sx={{ mr: 1 }} />,
            }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => setOpenRegisterDialog(true)}
          >
            メーカー登録
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

<DataGrid
      rows={manufacturers ?? []}
      onRowDoubleClick={(params) => {
        navigate(
          `${PATHS.MANUFACTURER_DETAIL.replaceAll(":id", params.id.toString())}`
        );
      }}
      columns={listColumns}
      loading={loading}
      sx={{
        cursor: "pointer",
        '& .MuiDataGrid-columnHeader': {
          paddingLeft: '20px',
        },
      }}
      localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      slotProps={{
        pagination: {
          labelRowsPerPage: "表示件数:",
        },
      }}
      pageSizeOptions={[5, 10, 25]}
      disableColumnSelector
      disableColumnFilter
      disableColumnMenu
      hideFooterSelectedRowCount
      disableDensitySelector
      disableRowSelectionOnClick
    />

      {/* 新規登録ダイアログ */}
      <Dialog
        open={openRegisterDialog}
        onClose={() => {
          if (!createLoading) {
            setOpenRegisterDialog(false);
            setNewManufacturerName("");
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>メーカー登録</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="メーカー名"
            value={newManufacturerName}
            onChange={(e) => setNewManufacturerName(e.target.value)}
            margin="normal"
            disabled={createLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenRegisterDialog(false);
              setNewManufacturerName("");
            }}
            disabled={createLoading}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={createLoading || !newManufacturerName.trim()}
          >
            {createLoading ? (
              <CircularProgress size={20} />
            ) : (
              "登録"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default ManufacturerList;
